import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  ApiRequestError,
  logout as apiLogout,
  type CurrentUser,
  clearStoredCsrfToken,
  currentUser,
  refreshCsrfToken,
} from "@/lib/api";
import { isUiFixtureMode } from "@/lib/dev-mode";

type SessionState =
  | { status: "loading" }
  | { status: "anonymous" }
  | { status: "authenticated"; user: CurrentUser };

interface SessionValue {
  state: SessionState;
  /** Re-checks `/user` — re-check after returning from platform sign-in. */
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ status: "loading" });

  const refresh = useCallback(async () => {
    try {
      const user = await currentUser();
      // Cookie session confirmed: mint the CSRF token mutations need. Best
      // effort — a failure here is retried on the first CSRF-rejected write.
      await refreshCsrfToken().catch(() => undefined);
      setState({ status: "authenticated", user });
    } catch (cause) {
      if (cause instanceof ApiRequestError && cause.status === 401) {
        setState({ status: "anonymous" });
      } else {
        // Network/server error: don't strand the visitor on an infinite
        // loading screen — treat as signed-out and let the guard
        // send them to platform sign-in, same as an expired session would look.
        setState({ status: "anonymous" });
      }
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signOut = useCallback(async () => {
    // Fixture mode models a persistent demo user; no auth state escapes the
    // tab, and signing out would leave protected routes on a blank spinner.
    if (isUiFixtureMode) return;
    try {
      await apiLogout();
    } finally {
      clearStoredCsrfToken();
      setState({ status: "anonymous" });
    }
  }, []);

  return (
    <SessionContext.Provider value={{ state, refresh, signOut }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value)
    throw new Error("useSession() must be used inside SessionProvider");
  return value;
}
